import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Factory } from "lucide-react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { GooglePlaceAutocomplete } from "@/components/ui/customs/autocomplete";
import { ControlledCountryComboboxCountryCode } from "@/components/ui/customs/controlled-combobox-countryCode";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { parseGooglePlaceAddress } from "@/lib/google-address.util";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";
import { GetAllCompanyDto } from "@/types/api/api-types";
import {
  MANUFACTURER_COUNTRY_CODE,
  QuotationUpdateBoFormData,
  ManufacturerInfoData,
  isManufacturerInfoRequiredFor,
} from "@/types/forms/bo/expeditionBo.form";

const EMPTY_MANUFACTURER_INFO: ManufacturerInfoData = {
  companyName: "",
  streetLines: ["", ""],
  city: "",
  postalCode: "",
  countryCode: MANUFACTURER_COUNTRY_CODE,
};

export const ManufacturerInfo = ({ company }: { company: GetAllCompanyDto }) => {
  const { t } = useTranslation("boExpeditionForm");
  const translateMessage = useTranslateMessage();
  const methods = useFormContext<QuotationUpdateBoFormData>();

  const billingEntityId = useWatch({ control: methods.control, name: "billingEntityId" });
  const destinationCountryCode = useWatch({
    control: methods.control,
    name: "deliveryInfo.countryCode",
  });
  const billingEntity = company.billingEntities.find((entity) => entity.id === billingEntityId);
  const isManufacturerInfoRequired = isManufacturerInfoRequiredFor(
    billingEntity?.country,
    destinationCountryCode,
  );

  useEffect(() => {
    const currentManufacturerInfo = methods.getValues("manufacturerInfo");
    if (isManufacturerInfoRequired && !currentManufacturerInfo) {
      methods.setValue(
        "manufacturerInfo",
        { ...EMPTY_MANUFACTURER_INFO, streetLines: ["", ""] },
        {
          shouldValidate: false,
        },
      );
    } else if (!isManufacturerInfoRequired && currentManufacturerInfo) {
      methods.setValue("manufacturerInfo", undefined, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManufacturerInfoRequired]);

  const handlePlaceSelect = useCallback(
    (place: google.maps.places.PlaceResult | null) => {
      const parsed = parseGooglePlaceAddress(place, MANUFACTURER_COUNTRY_CODE);
      if (!parsed) return;

      methods.setValue("manufacturerInfo.streetLines.0", parsed.street);
      methods.setValue("manufacturerInfo.city", parsed.city);
      methods.setValue("manufacturerInfo.postalCode", parsed.postalCode);
    },
    [methods],
  );

  if (!isManufacturerInfoRequired) return null;

  return (
    <Accordion type="single" collapsible className="flex w-full @3xl:col-span-2">
      <AccordionItem
        value="manufacturerInfo"
        className="flex w-full flex-col rounded-lg border border-slate-300 bg-white px-2 shadow-sm @xl:px-3"
      >
        <AccordionTrigger className="items-center gap-3 py-3 !no-underline hover:no-underline">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
            <div className="flex flex-row items-center gap-2">
              <Factory className="h-6 w-6 shrink-0 !rotate-0 text-blue-600" />
              <span className="text-lg font-bold">{t("manufacturerInfo.title")}</span>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent forceMount className="flex w-full flex-col gap-2">
          <ControlledUnifiedInput
            control={methods.control}
            name="manufacturerInfo.companyName"
            label={t("manufacturerInfo.companyNameLabel")}
            required
            wrapperClassName="w-full"
          />
          <ControlledCountryComboboxCountryCode
            label={t("manufacturerInfo.countryLabel")}
            required
            control={methods.control}
            name="manufacturerInfo.countryCode"
            wrapperClassName="w-full"
            isDisabled
          />
          <Controller
            name="manufacturerInfo.streetLines.0"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <GooglePlaceAutocomplete
                label={t("manufacturerInfo.streetLabel")}
                required
                value={field.value || ""}
                onInputChange={(val) => field.onChange(val)}
                onPlaceSelect={handlePlaceSelect}
                error={translateMessage(error?.message)}
                wrapperClassName="w-full min-w-0"
                countryRestrictions={[MANUFACTURER_COUNTRY_CODE]}
              />
            )}
          />
          <ControlledUnifiedInput
            control={methods.control}
            name="manufacturerInfo.streetLines.1"
            label={t("manufacturerInfo.addressComplementLabel")}
            className="w-full"
            wrapperClassName="w-full"
          />
          <div className="flex flex-col gap-2 @xl:flex-row">
            <ControlledUnifiedInput
              className="w-full"
              control={methods.control}
              name="manufacturerInfo.postalCode"
              label={t("manufacturerInfo.postalCodeLabel")}
              required
              wrapperClassName="w-full"
            />
            <ControlledUnifiedInput
              className="flex-1"
              control={methods.control}
              name="manufacturerInfo.city"
              label={t("manufacturerInfo.cityLabel")}
              required
              wrapperClassName="w-full"
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
